import {React, Fragment, observer} from '@api'
import {MenuDivider} from '@blueprintjs/core'
import {Image} from '@material-ui/icons'
import {ContextMenuItem, ContextMenuItemProps} from '@modules'
import {whiteboardIcons} from '@modules/whiteboard/icons'
import {useWhiteboardStore} from '@modules/whiteboard'
import {AlignBottom, AlignLeft, AlignRight, AlignTop, CenterHorizontal, CenterVertical, DistributeHorizontal, DistributeVertical, StretchHorizontal, StretchVertical} from '~components/ToolsPanel/ActionButton'
import {StyleMenuPanel} from '~components/TopPanel/StyleMenu'
import * as css from './WhiteboardContextMenu.css'

export const WhiteboardContextMenu = observer(function WhiteboardContextMenu() {
  const {hasMultipleShapesSelected, hasGroupSelected} = useWhiteboardStore()

  return (<Fragment>
    <NewShapeMenuItem/>
    <StyleSelectionMenuItem dividerAfter/>
    <DuplicateShapesMenuItem/>
    <FlipHorizontalMenuItem/>
    <FlipVerticalMenuItem/>
    <LockUnlockShapesMenuItem/>

    {(hasMultipleShapesSelected || hasGroupSelected) &&
	 <Fragment>
		 <MenuDivider/>
		 <GroupShapesMenuItem/>
		 <UngroupShapesMenuItem/>
	 </Fragment>}

    <MenuDivider/>
    <MoveShapesMenuItem/>
    <MoveToPageMenuItem/>
    <MenuDivider/>

    <AlignDistributeMenuItem/>
    <MenuDivider/>
    <CutShapesMenuItem/>
    <CopyShapesMenuItem/>
    <CopyShapesAsSVGMenuItem/>
    <CopyShapesAsJSONMenuItem/>
    <PasteShapesMenuItem/>
    <MenuDivider/>
    <DeleteShapesMenuItem/>

    <UndoMenuItem/>
    <RedoMenuItem/>

    <BackgroundMenuItem/>
  </Fragment>)
})

export const BackgroundMenuItem = observer(function BackgroundMenuItem() {
  const store = useWhiteboardStore(),
    {myPageState: ps, myPageState: {background}} = store

  return (<ContextMenuItem icon={<Image/>} text={'Background'}>
    <ContextMenuItem text={'None'} active={!background || background === 'none'} onClick={() => ps.background = null}/>
    <ContextMenuItem text={'Blueprint'} active={background === 'blueprint'} onClick={() => ps.background = 'blueprint'}/>
    <ContextMenuItem text={'Checkerboard'} active={background === 'checkerboard'} onClick={() => ps.background = 'checkerboard'}/>
  </ContextMenuItem>)
})

export const NewShapeMenuItem = observer<ContextMenuItemProps>(function NewShapeMenuItem(props) {
  const {selectedShapes} = useWhiteboardStore()

  return <ContextMenuItem icon={'new-object'} text={'New'}>
    {/*<ContextMenuItem icon='shapes' title={'Primitives'}>*/}
      <NewRectangleMenuItem/>
      <NewCircleMenuItem/>
      <NewLineMenuItem/>
    {/*</ContextMenuItem>*/}

  </ContextMenuItem>
})

export const NewRectangleMenuItem = observer<ContextMenuItemProps>(function NewRectangleMenuItem(props) {
  const {createUtil: {createRectangle}} = useWhiteboardStore()
  return <ContextMenuItem text='Rectangle' shouldDismissPopover icon={whiteboardIcons.rect} onClick={createRectangle}/>
})

export const NewCircleMenuItem = observer<ContextMenuItemProps>(function NewCircleMenuItem(props) {
  const {createUtil: {createEllipse}} = useWhiteboardStore()
  return <ContextMenuItem text='Ellipse' shouldDismissPopover icon={whiteboardIcons.ellipse} onClick={createEllipse}/>
})

export const NewLineMenuItem = observer<ContextMenuItemProps>(function NewLineMenuItem(props) {
  const {createUtil: {createLine}} = useWhiteboardStore()
  return <ContextMenuItem text='Line' shouldDismissPopover icon={whiteboardIcons.line} onClick={createLine}/>
})

export const StyleSelectionMenuItem = observer<ContextMenuItemProps>(function StyleSelectionMenuItem(props) {
  const {selectedShapes} = useWhiteboardStore()

  return _.isEmpty(selectedShapes)
         ? null
         : (
           <ContextMenuItem
             {...props}
             noSubmenuPadding
             text={'Style...'}
           >
             <StyleMenuPanel variant='menu'/>
           </ContextMenuItem>)
})
export const DuplicateShapesMenuItem = observer(function DuplicateShapesMenuItem() {
  const {selectedShapes, duplicate, strings} = useWhiteboardStore()

  return _.isEmpty(selectedShapes)
         ? null
         : <ContextMenuItem text={strings.duplicate}
                            onClick={() => duplicate()}
                            kbd='#D'
         />
})

export const FlipHorizontalMenuItem = observer(function FlipHorizontalMenuItem() {
  const {selectedShapes, flipHorizontal, strings} = useWhiteboardStore()

  return _.isEmpty(selectedShapes)
         ? null
         : <ContextMenuItem text={'Flip Horizontal'}
                            onClick={() => flipHorizontal()}
                            kbd='⇧H'
         />
})

export const FlipVerticalMenuItem = observer(function FlipVerticalMenuItem() {
  const {selectedShapes, flipVertical, strings} = useWhiteboardStore()

  return _.isEmpty(selectedShapes)
         ? null
         : <ContextMenuItem text={'Flip Vertical'}
                            onClick={() => flipVertical()}
                            kbd='⇧H'
         />
})

export const LockUnlockShapesMenuItem = observer(function LockUnlockShapesMenuItem() {
  const {selectedShapes, toggleLocked} = useWhiteboardStore()

  return _.isEmpty(selectedShapes)
         ? null
         : <ContextMenuItem text={'Lock / Unlock'}
                            onClick={() => toggleLocked()}
                            kbd='#⇧L'
         />
})

export const GroupShapesMenuItem = observer(function GroupShapesMenuItem() {
  const {group, hasMultipleShapesSelected} = useWhiteboardStore()

  return !hasMultipleShapesSelected
         ? null
         : <ContextMenuItem text={'Group'}
                            onClick={() => group()}
                            kbd='#G'
         />
})

export const UngroupShapesMenuItem = observer(function UngroupShapesMenuItem() {
  const {ungroup, hasGroupSelected} = useWhiteboardStore()

  return !hasGroupSelected
         ? null
         : <ContextMenuItem text={'Group'}
                            onClick={() => ungroup()}
                            kbd='#⇧G'
         />
})

export const MoveShapesMenuItem = observer(function MoveShapesMenuItem() {
  const {hasSelection} = useWhiteboardStore()

  if (!hasSelection) return null

  return (
    <ContextMenuItem text='Move'>
      <MoveToFrontMenuItem/>
      <MoveForwardMenuItem/>
      <MoveBackwardMenuItem/>
      <MoveToBackMenuItem/>
    </ContextMenuItem>
  )
})

export const MoveToFrontMenuItem = observer(function MoveToFrontMenuItem() {
  const {moveToFront} = useWhiteboardStore()

  return <ContextMenuItem
    text={'To Front'}
    onClick={() => moveToFront()}
    kbd=']'
  />
})

export const MoveForwardMenuItem = observer(function MoveForwardMenuItem() {
  const {moveForward} = useWhiteboardStore()

  return <ContextMenuItem
    text={'Forward'}
    onClick={() => moveForward()}
    kbd=']'
  />
})

export const MoveBackwardMenuItem = observer(function MoveBackwardMenuItem() {
  const {moveBackward} = useWhiteboardStore()

  return <ContextMenuItem
    text={'Backward'}
    onClick={() => moveBackward()}
    kbd='['
  />
})

export const MoveToBackMenuItem = observer(function MoveToBackMenuItem() {
  const {moveToBack} = useWhiteboardStore()

  return <ContextMenuItem
    text={'To Back'}
    onClick={() => moveToBack()}
    kbd='⇧['
  />
})

export const MoveToPageMenuItem = observer(function MoveShapesMenuItem() {
  const {hasSelection} = useWhiteboardStore()

  if (!hasSelection) return null

  return (
    <ContextMenuItem text='Move to Page'>

    </ContextMenuItem>
  )
})

/// region Align / Distribute
export const AlignDistributeMenuItem = observer(function AlignDistributeMenuItem() {
  const {hasMultipleShapesSelected, selectedShapes} = useWhiteboardStore()

  /*



          <CMIconButton onClick={alignTop}>
            <AlignTopIcon />
          </CMIconButton>
          <CMIconButton onClick={alignCenterVertical}>
            <AlignCenterVerticallyIcon />
          </CMIconButton>
          <CMIconButton onClick={alignBottom}>
            <AlignBottomIcon />
          </CMIconButton>
          <CMIconButton onClick={stretchVertically}>
            <StretchVerticallyIcon />
          </CMIconButton>
          {hasThreeOrMore && (
            <CMIconButton onClick={distributeVertically}>
              <SpaceEvenlyVerticallyIcon />
            </CMIconButton>
          )}
   */
  return <ContextMenuItem noSubmenuPadding text={'Align / Distribute'}>
           <div className={css.alignDistributePanel} data-selected={selectedShapes.length}>
             <AlignLeft />
             <CenterHorizontal />
             <AlignRight />
             <StretchHorizontal />
             <DistributeHorizontal />
             <AlignTop />
             <CenterVertical />
             <AlignBottom />
             <StretchVertical />
             <DistributeVertical />
           </div>
         </ContextMenuItem>
})


export const AlignLeftMenuItem = observer(function AlignLeftMenuItem() {
  const {hasSelection, align} = useWhiteboardStore()

  return <ContextMenuItem
    disabled={!hasSelection}
    icon={'alignment-left'}
    onClick={() => align('left')}
  />
})

export const AlignCenterHorMenuItem = observer(function AlignCenterHorMenuItem() {
  const {hasSelection, align} = useWhiteboardStore()

  return <ContextMenuItem
    disabled={!hasSelection}
    icon={'alignment-horizontal-center'}
    text={'Align Horizontal Center'}
    onClick={() => align('centerHorizontal')}
  />
})

export const AlignRightMenuItem = observer(function AlignRightMenuItem() {
  const {hasSelection, align} = useWhiteboardStore()

  return <ContextMenuItem
    disabled={!hasSelection}
    icon={'alignment-right'}
    text={'Align Right'}
    onClick={() => align('right')}
  />
})

export const StretchHorMenuItem = observer(function StretchHorMenuItem() {
  const {hasSelection, stretch} = useWhiteboardStore()

  return <ContextMenuItem
    disabled={!hasSelection}
    icon={'arrows-horizontal'}
    text={'Stretch Horizontal'}
    onClick={() => stretch('horizontal')}
  />
})
export const StretchVerMenuItem = observer(function StretchVerMenuItem() {
  const {hasSelection, stretch} = useWhiteboardStore()

  return <ContextMenuItem
    disabled={!hasSelection}
    icon={'arrows-vertical'}
    text={'Stretch Vertical'}
    onClick={() => stretch('vertical')}
  />
})

export const DistributeHorMenuItem = observer(function DistributeHorMenuItem() {
  const {hasSelection, distribute} = useWhiteboardStore()

  return <ContextMenuItem
    disabled={!hasSelection}
    icon={'arrows-horizontal'}
    text={'Distribute Horizontal'}
    onClick={() => distribute('horizontal')}
  />
})

export const DistributeVerMenuItem = observer(function DistributeVerMenuItem() {
  const {hasSelection, distribute} = useWhiteboardStore()

  return <ContextMenuItem
    disabled={!hasSelection}
    icon={'arrows-vertical'}
    text={'Distribute Vertical'}
    onClick={() => distribute('vertical')}
  />
})

export const AlignTopMenuItem = observer(function AlignTopMenuItem() {
  const {hasSelection, align} = useWhiteboardStore()

  return <ContextMenuItem
    disabled={!hasSelection}
    icon={'alignment-top'}
    text={'Align Top'}
    onClick={() => align('top')}
  />
})

export const AlignCenterVerMenuItem = observer(function AlignCenterHorMenuItem() {
  const {hasSelection, align} = useWhiteboardStore()

  return <ContextMenuItem
    disabled={!hasSelection}
    icon={'alignment-vertical-center'}
    text={'Align Vertical Center'}
    onClick={() => align('centerVertical')}
  />
})

export const AlignBottomMenuItem = observer(function AlignBottomMenuItem() {
  const {hasSelection, align} = useWhiteboardStore()

  return <ContextMenuItem
    disabled={!hasSelection}
    icon={'alignment-bottom'}
    text={'Align Bottom'}
    onClick={() => align('bottom')}
  />
})


/// endregion

export const CutShapesMenuItem = observer(function CutShapesMenuItem() {
  const {hasSelection, clipboard: {cut}} = useWhiteboardStore()

  return hasSelection && <ContextMenuItem
	  text={'Cut'}
	  onClick={() => cut()}
	  kbd='#X'
  />
})

export const CopyShapesMenuItem = observer(function CopyShapesMenuItem() {
  const {hasSelection, clipboard: {copy}} = useWhiteboardStore()

  return hasSelection && <ContextMenuItem
	  text={'Copy'}
	  onClick={() => copy()}
	  kbd='#C'
  />
})

export const CopyShapesAsSVGMenuItem = observer(function CopyShapesAsSVGMenuItem() {
  const {hasSelection, clipboard: {copySvg}} = useWhiteboardStore()

  return hasSelection && <ContextMenuItem
	  text={'Copy as SVG'}
	  onClick={() => copySvg()}
	  kbd='#⇧C'
  />
})

export const CopyShapesAsJSONMenuItem = observer(function CopyShapesAsJSONMenuItem() {
  const {hasSelection, clipboard: {copyJson}, ui: {isDebugMode}} = useWhiteboardStore()

  return hasSelection && isDebugMode ? <ContextMenuItem
    text={'Copy as JSON'}
    onClick={() => copyJson()}
    kbd='#⇧C'
  /> : null
})

export const PasteShapesMenuItem = observer(function PasteShapesMenuItem() {
  const {clipboard: {hasClipboardContents, paste}} = useWhiteboardStore()

  return <ContextMenuItem
    text={'Paste'}
    //    disabled={!hasClipboardContents && !navigator.has}  // No way to know if there's something on the user's clipboard
    onClick={() => paste()}
    kbd='#V'
  />
})

export const DeleteShapesMenuItem = observer(function DeleteShapesMenuItem() {
  const {hasSelection, deleteShapes} = useWhiteboardStore()

  return hasSelection && <ContextMenuItem
	  text={'Delete'}
	  onClick={() => deleteShapes()}
	  kbd='⌫'
  />
})

export const UndoMenuItem = observer(function UndoMenuItem() {
  const {hasSelection, undo} = useWhiteboardStore()

  return hasSelection && <ContextMenuItem
	  text={'Undo'}
	  onClick={() => undo()}
	  kbd='#Z'
  />
})

export const RedoMenuItem = observer(function RedoMenuItem() {
  const {hasSelection, redo} = useWhiteboardStore()

  return hasSelection && <ContextMenuItem
	  text={'Redo'}
	  onClick={() => redo()}
	  kbd='#⇧Z'
  />
})


// WEBPACK FOOTER //
// ./src/modules/whiteboard/WhiteboardContextMenu.tsx